import { BorderRadiusOption, SpacingOption } from 'omakase/PadButton/padButtonTypes';
import { PadButtonNumColumns } from 'omakase/PadButton/PadButtonGroup';
import { PadButtonSelectedVariant, PadButtonVariant } from './PadButtonCore';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';

const FOCUS_RING_SIZE = '2px';
const FOCUS_RING_SEPARATION = `-${FOCUS_RING_SIZE}`;
const SVG_WIDTH = 64;
const GROUP_RADIUS_SM = 12;
const GROUP_RADIUS_LG = 48;

interface PadButtonState {
  checked: boolean;
  disabled: boolean;
  selectedVariant: PadButtonSelectedVariant;
}

interface PadButtonColorStyles {
  backgroundColor: string;
  boxShadowInnerBorderColor: string;
  boxShadowInnerBorderWidth: string;
  boxShadowElevation: string;
}

export type PadButtonInteractionType = 'enabled' | 'hover' | 'active' | 'disabled';

export const padButtonContainer = () => {
  return css`
    background: transparent;
    flex: 1;
    position: relative;
  `;
};

export const padButtonBaseComponent = ({ shadow, colors, zIndex }: Theme, checked: boolean) => {
  return css`
    position: absolute;
    opacity: 0;
    z-index: -${zIndex.bump};

    body.no-focus-visible &:focus + label::after {
      box-shadow: 0 0 0 ${FOCUS_RING_SIZE} ${colors.borderPrimaryBase};
    }

    &:focus-visible + label::after {
      box-shadow: 0 0 0 ${FOCUS_RING_SIZE} ${colors.borderPrimaryBase};
    }

    &:disabled + label {
      --background-color: ${checked ? colors.bgDefault : 'transparent'};
      --box-shadow-inner-border-color: ${checked ? colors.borderDefault : 'transparent'};
      --box-shadow-elevation: ${checked ? shadow[100] : '0 1px 0 0 rgba(0, 0, 0, 0)'};
      pointer-events: none;
    }
  `;
};

interface PadButtonBorderOptions {
  border: boolean;
  borderRadius: BorderRadiusOption;
}

export const padButtonLabel = (
  theme: Theme,
  variant: PadButtonVariant,
  verticalSpacing: SpacingOption,
  { border, borderRadius }: PadButtonBorderOptions,
  { checked, disabled, selectedVariant }: PadButtonState
) => {
  const {
    backgroundColor,
    boxShadowInnerBorderColor,
    boxShadowInnerBorderWidth,
    boxShadowElevation,
  } = getPadButtonBackgroundColorsAndElevation(theme, variant, selectedVariant, 'enabled', {
    checked,
    border,
  });

  const mainBorderRadius = borderRadius === 'small' ? '12px' : '24px';

  return css`
    --background-color: ${backgroundColor};
    --box-shadow-inner-border-color: ${boxShadowInnerBorderColor};
    --box-shadow-inner-border-width: ${boxShadowInnerBorderWidth};
    --box-shadow-elevation: ${boxShadowElevation};

    background: var(--background-color);
    box-shadow:
      inset 0px 0px 0px var(--box-shadow-inner-border-width) var(--box-shadow-inner-border-color),
      var(--box-shadow-elevation);
    border-radius: ${mainBorderRadius};
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: ${verticalSpacing === 'large' ? '20px' : '15px'} 0; // Specific px values are intended, per Figma / design discussion
    position: relative;
    transition: all ${theme.timings.duration.shortest} ${theme.timings.timingFunction.easeInOut};

    ${getInteractiveStates(theme, variant, { checked, disabled, selectedVariant }, { border })}

    & > svg {
      width: ${SVG_WIDTH}px;
    }

    &::after {
      bottom: ${FOCUS_RING_SEPARATION};
      border-radius: ${mainBorderRadius};
      content: '';
      left: ${FOCUS_RING_SEPARATION};
      pointer-events: none;
      position: absolute;
      right: ${FOCUS_RING_SEPARATION};
      top: ${FOCUS_RING_SEPARATION};
    }
  `;
};

// Exported for testing
export const getPadButtonBackgroundColorsAndElevation = (
  { colors, shadow }: Theme,
  variant: PadButtonVariant,
  selectedVariant: PadButtonSelectedVariant,
  interactionType: PadButtonInteractionType,
  { checked, border }: { checked: boolean; border: boolean }
): PadButtonColorStyles => {
  let backgroundColor;

  switch (interactionType) {
    case 'enabled':
      if (checked) {
        backgroundColor = selectedVariant === 'basic' ? colors.bgDefault : colors.bgDarker;
      } else {
        backgroundColor = 'transparent';
      }
      break;
    case 'hover':
      if (checked) {
        backgroundColor = selectedVariant === 'basic' ? colors.bgLighter : colors.bgContrast;
      } else {
        backgroundColor = variant === 'basic' ? colors.bgLighter : colors.bgDefault;
      }
      break;
    case 'active':
      backgroundColor =
        checked && selectedVariant === 'highContrast' ? colors.bgContrast : colors.bgLight;
      break;
    case 'disabled':
      backgroundColor = checked ? colors.bgDefault : 'transparent';
      break;
    default:
      assertNever(interactionType);
  }

  const boxShadowInnerBorderColor =
    interactionType === 'disabled'
      ? colors.fgDisabled
      : checked && selectedVariant === 'basic'
        ? colors.borderPrimaryBase
        : border
          ? colors.borderDefault
          : backgroundColor;

  const boxShadowInnerBorderWidth = checked ? '2px' : '1px';

  const boxShadowElevation =
    interactionType === 'hover' ||
    (checked && (interactionType === 'enabled' || interactionType === 'disabled'))
      ? shadow[100]
      : '0 1px 0 0 rgba(0, 0, 0, 0)';

  return {
    backgroundColor,
    boxShadowInnerBorderColor,
    boxShadowInnerBorderWidth,
    boxShadowElevation,
  };
};

export const getInteractiveStates = (
  theme: Theme,
  variant: PadButtonVariant,
  { checked, selectedVariant }: PadButtonState,
  { border }: { border: boolean }
) => {
  const hover = getPadButtonBackgroundColorsAndElevation(theme, variant, selectedVariant, 'hover', {
    checked,
    border,
  });
  const active = getPadButtonBackgroundColorsAndElevation(
    theme,
    variant,
    selectedVariant,
    'active',
    { checked, border }
  );
  const disabled = getPadButtonBackgroundColorsAndElevation(
    theme,
    variant,
    selectedVariant,
    'disabled',
    { checked, border }
  );

  return [
    theme.interactiveStates.hover(css`
      --background-color: ${hover.backgroundColor};
      --box-shadow-inner-border-color: ${hover.boxShadowInnerBorderColor};
      --box-shadow-elevation: ${hover.boxShadowElevation};
    `),
    theme.interactiveStates.active(css`
      --background-color: ${active.backgroundColor};
      --box-shadow-inner-border-color: ${active.boxShadowInnerBorderColor};
      --box-shadow-elevation: ${active.boxShadowElevation};
    `),
    theme.interactiveStates.disabled(css`
      --background-color: ${disabled.backgroundColor};
      --box-shadow-inner-border-color: ${disabled.boxShadowInnerBorderColor};
      --box-shadow-elevation: ${disabled.boxShadowElevation};
    `),
  ];
};

// TODO(bp-tock): type the return value as : PrimitiveInterpolation[] when that typing becomes available.
export const padButtonTypography = (
  theme: Theme,
  padButtonTypographyOptions: PadButtonState,
  { hasDecorativeElement }: { hasDecorativeElement: boolean }
) => {
  return [
    getTextColorFromVariantAndState(theme, padButtonTypographyOptions),
    css`
      margin-top: ${hasDecorativeElement ? theme.spacing(1) + 'px' : 0};
      overflow: hidden;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    `,
    theme.typography.variant('body02Strong'),
  ];
};

export const getTextColorFromVariantAndState = (
  { colors }: Theme,
  textColorOptions: PadButtonState
) => {
  if (textColorOptions.disabled) {
    return css`
      color: ${colors.fgDisabled};
    `;
  }

  if (textColorOptions.checked) {
    if (textColorOptions.selectedVariant === 'highContrast') {
      return css`
        color: ${colors.fgContrast};
      `;
    } else {
      return css`
        color: ${colors.fgPrimaryBase};
      `;
    }
  }

  return css`
    color: ${colors.fgDefault};
  `;
};

interface PadButtonGroupStyleOptions {
  background: boolean;
  columnGap: number;
}

export const padButtonGroup = (
  theme: Theme,
  numColumns: PadButtonNumColumns,
  { background, columnGap }: PadButtonGroupStyleOptions
) => {
  return css`
    background: ${getButtonGroupBackground(theme, background)};
    border-radius: ${GROUP_RADIUS_SM}px;
    display: grid;
    grid-gap: ${theme.spacing(columnGap)}px;
    grid-template-columns: ${getGridTemplateColumnsString(numColumns)};
    padding: ${theme.spacing(2.5)}px;
  `;
};

export const switcherGroup = (theme: Theme, background: boolean) => {
  return css`
    background: ${getButtonGroupBackground(theme, background)};
    border-radius: ${GROUP_RADIUS_LG}px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${theme.spacing(1.5)}px;
  `;
};

const getButtonGroupBackground = ({ colors }: Theme, background: boolean) => {
  return background ? colors.bgLighter : 'transparent';
};

/**
 * Generate the value for the grid-template-columns property
 */
const getGridTemplateColumnsString = (numColumns: PadButtonNumColumns) => {
  return Array.from(Array(numColumns)).reduce<string>(
    (prev, _current) => (prev += 'minmax(0, 1fr) '),
    ''
  );
};

export const switcherGroupDivider = (
  { colors, spacing, timings }: Theme,
  { hide }: { hide: boolean }
) => {
  return css`
    background: ${hide ? 'transparent' : colors.borderDefault};
    height: ${spacing(2.5)}px;
    margin: 0 ${spacing(0.5)}px;
    transition: all ${timings.duration.shortest} ${timings.timingFunction.easeInOut};
    width: 1px;
  `;
};
